import React, { forwardRef, useState } from 'react';
import styles from './text-field.module.scss';
import classNames from 'classnames';

interface BaseTextFieldProps {
    label: string;
    error?: string;
    helperText?: string;
    required?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    onValidate?: (value: string) => string | undefined;
    multiline?: boolean;
    rows?: number;
}

type TextFieldProps = BaseTextFieldProps &
    (
        | ({ multiline: true } & React.TextareaHTMLAttributes<HTMLTextAreaElement>)
        | ({ multiline?: false } & React.InputHTMLAttributes<HTMLInputElement>)
    );

const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
    (
        {
            label,
            error,
            helperText,
            required,
            leftIcon,
            rightIcon,
            onValidate,
            id,
            multiline,
            rows = 3,
            ...props
        },
        ref
    ) => {
        const [localError, setLocalError] = useState<string>();
        const [touched, setTouched] = useState(false);
        const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);

        const fieldId = id || label.toLowerCase().replace(/\s+/g, '-');
        const errorId = `${fieldId}-error`;
        const helperId = `${fieldId}-helper`;

        const displayError = error || (touched && localError);

        const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setTouched(true);
            if (onValidate) {
                const validationError = onValidate(e.target.value);
                setLocalError(validationError);
            }
            props.onBlur?.(e as any);
        };

        return (
            <div
                className={classNames([
                    styles.textField,
                    props.className,
                    props.disabled && styles.disabled,
                ])}
            >
                <label htmlFor={fieldId} className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>

                <div>
                    {multiline ? (
                        <div
                            className={classNames([
                                styles.inputRoot,
                                displayError && styles.errorInput,
                            ])}
                            onPointerDown={(event) => {
                                const target = event.target as HTMLElement;
                                if (target.closest('input, button, a')) return;

                                const input = inputRef.current;
                                if (!input) return;

                                // Same selector as in the CSS to find the right slot
                                const isRightSlot = target.closest(`
                          .rt-TextFieldSlot[data-side='right'],
                          .rt-TextFieldSlot:not([data-side='right']) ~ .rt-TextFieldSlot:not([data-side='left'])
                        `);

                                const cursorPosition = isRightSlot ? input.value.length : 0;

                                requestAnimationFrame(() => {
                                    // Only some input types support this, browsers will throw an error if not supported
                                    // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange#:~:text=Note%20that%20according,not%20support%20selection%22.
                                    try {
                                        input.setSelectionRange(cursorPosition, cursorPosition);
                                    } catch {}
                                    input.focus();
                                });
                            }}
                        >
                            <textarea
                                {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                                ref={ref as React.Ref<HTMLTextAreaElement>}
                                id={fieldId}
                                rows={rows}
                                aria-invalid={!!displayError}
                                aria-required={required}
                                aria-describedby={`${displayError ? errorId : ''} ${
                                    helperText ? helperId : ''
                                }`}
                                className={classNames([styles.input])}
                                onBlur={handleBlur}
                            />
                        </div>
                    ) : (
                        <div
                            className={classNames([
                                styles.inputRoot,
                                displayError && styles.errorInput,
                            ])}
                            onPointerDown={(event) => {
                                const target = event.target as HTMLElement;
                                if (target.closest('input, button, a')) return;

                                const input = inputRef.current;
                                if (!input) return;

                                // Same selector as in the CSS to find the right slot
                                const isRightSlot = target.closest(`
                                .rt-TextFieldSlot[data-side='right'],
                                .rt-TextFieldSlot:not([data-side='right']) ~ .rt-TextFieldSlot:not([data-side='left'])
                              `);

                                const cursorPosition = isRightSlot ? input.value.length : 0;

                                requestAnimationFrame(() => {
                                    // Only some input types support this, browsers will throw an error if not supported
                                    // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange#:~:text=Note%20that%20according,not%20support%20selection%22.
                                    try {
                                        input.setSelectionRange(cursorPosition, cursorPosition);
                                    } catch {}
                                    input.focus();
                                });
                            }}
                        >
                            {leftIcon && <div className={styles.slot}>{leftIcon}</div>}
                            <input
                                {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
                                ref={ref as React.Ref<HTMLInputElement>}
                                id={fieldId}
                                aria-invalid={!!displayError}
                                aria-required={required}
                                aria-describedby={`${displayError ? errorId : ''} ${
                                    helperText ? helperId : ''
                                }`}
                                className={classNames([styles.input])}
                                onBlur={handleBlur}
                            />
                            {rightIcon && <div className={styles.slot}>{rightIcon}</div>}
                        </div>
                    )}
                </div>

                {displayError && (
                    <p
                        id={errorId}
                        className={classNames([styles.helperText, styles.error])}
                        role="alert"
                    >
                        {displayError}
                    </p>
                )}

                {helperText && !displayError && (
                    <p id={helperId} className={styles.helperText}>
                        {helperText}
                    </p>
                )}
            </div>
        );
    }
);

TextField.displayName = 'TextField';

export default TextField;
